import './App.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import CartProvider from './contexts/CartContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { RestaurantAuthProvider } from './contexts/RestaurantAuthContext';

// Pages publiques
import Landing from './pages/landing';
import Home from './pages/home';
import Store from './pages/store';
import RestaurantDetails from './pages/restaurant-details';
import Restaurants from './pages/restaurants';
import Cart from './pages/cart';
import Checkout from './pages/checkout';
import CheckoutPay from './pages/checkout-pay';
import Product from './pages/product';
import GroceryShopProductPage from './pages/grocery-shop-product';
import GroceryShopDetails from './pages/grocery-shop-details';
import Signup from './pages/signup';
import RestaurantSignup from './pages/restaurant-signup';
import Login from './pages/login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Profile from './pages/profilpage';
import AboutUs from './pages/about-us';
import TermsOfService from './pages/terms-of-service';
import PrivacyPolicy from './pages/privacy-policy';
import Impressum from './pages/impressum';
import Contact from './pages/contact';
import Traiteur from './pages/traiteur';
import CateringServiceDetails from './pages/catering-service-details';
import OrderConfirmation from './pages/order-confirmation';
import NotFound from './pages/NotFound';

// Pages de login
import AdminLogin from './pages/admin-login';
import RestaurantLogin from './pages/restaurant-login';

// Composants protégés
import { AdminProtectedRoute, PermissionProtectedRoute, SuperAdminProtectedRoute } from './components/AdminProtectedRoute';
import RestaurantProtectedRoute from './components/RestaurantProtectedRoute';

// Layouts
import AdminLayout from './components/Dashboard/AdminLayout';
import RestaurantLayout from './components/Dashboard/RestaurantLayout';

// Pages Admin
import AdminDashboard from './components/Dashboard/pages/AdminDashboard';
import AdminUsers from './components/Dashboard/pages/AdminUsers';
import AdminRestaurants from './components/Dashboard/pages/AdminRestaurants';
import AdminGroceryShops from './components/Dashboard/pages/AdminGroceryShops';
import AdminDishes from './components/Dashboard/pages/AdminDishes';
import AdminOrders from './components/Dashboard/pages/AdminOrders';
import AdminComplaints from './components/Dashboard/pages/AdminComplaints';
import AdminPayments from './components/Dashboard/pages/AdminPayments';
import AdminAdmins from './components/Dashboard/pages/AdminAdmins';
import AdminSettings from './components/Dashboard/pages/AdminSettings';
import AdminPromotionInsights from './components/Dashboard/pages/AdminPromotionInsights';
import AdminPromotionPricing from './components/Dashboard/pages/AdminPromotionPricing';
import AdminAnnouncements from './components/Dashboard/pages/AdminAnnouncements';
import AdminReferenceData from './components/Dashboard/pages/AdminReferenceData';

// Pages Restaurant
import NewRestaurantDashboard from './components/Dashboard/pages/NewRestaurantDashboard';
import NewRestaurantOrders from './components/Dashboard/pages/NewRestaurantOrders';
import RestaurantMenu from './components/Dashboard/pages/RestaurantMenu';
import RestaurantGrocery from './components/Dashboard/pages/RestaurantGrocery';
import NewRestaurantAnalytics from './components/Dashboard/pages/NewRestaurantAnalytics';
import NewRestaurantWallet from './components/Dashboard/pages/NewRestaurantWallet';
import NewRestaurantEmployees from './components/Dashboard/pages/NewRestaurantEmployees';
import NewRestaurantSettings from './components/Dashboard/pages/NewRestaurantSettings';
import NewRestaurantCatering from './components/Dashboard/pages/NewRestaurantCatering';
import CateringServiceOfferDetail from './components/Dashboard/pages/CateringServiceOfferDetail';
import NewRestaurantPromotions from './components/Dashboard/pages/NewRestaurantPromotions';
import NewRestaurantReviews from './components/Dashboard/pages/NewRestaurantReviews';
import RestaurantAnnouncements from './components/Dashboard/pages/RestaurantAnnouncements';
import PromotionCheckout from './pages/promotion-checkout';

// /traiteur/:slug existait comme page dédiée par formule ; désormais /traiteur est
// l'annuaire unique des cuisiniers traiteurs, filtrable par formule via ?formula=.
function TraiteurFormuleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/traiteur?formula=${slug}`} replace />;
}

function RootEntry() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const checkoutMode = params.get('checkout');

  if (checkoutMode === 'pay') {
    return <CheckoutPay />;
  }

  return <Home />;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <CartProvider>
        <AdminAuthProvider>
          <RestaurantAuthProvider>
            <div className="App">
              <ErrorBoundary>
              <Routes>
                {/* Routes publiques */}
                <Route path="/" element={<RootEntry />} />
                <Route path="/feed" element={<Home />} />
                <Route path="/store" element={<Store />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/restaurants" element={<Restaurants />} />
                <Route path="/restaurant/:id" element={<RestaurantDetails />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout-pay" element={<CheckoutPay />} />
                <Route path="/product/:id" element={<Product />} />
                <Route path="/grocery-shop/:id" element={<GroceryShopDetails />} />
                <Route path="/grocery-shop-product/:id" element={<GroceryShopProductPage />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/business/signup" element={<RestaurantSignup />} />
                <Route path="/restaurant-signup" element={<Navigate to="/business/signup" replace />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/catering-services" element={<Navigate to="/traiteur" replace />} />
                <Route path="/traiteur" element={<Traiteur />} />
                <Route path="/traiteur/:slug" element={<TraiteurFormuleRedirect />} />
                <Route path="/catering-service-details/:id" element={<CateringServiceDetails />} />
                <Route path="/event-catering-request" element={<Navigate to="/traiteur" replace />} />
                <Route path="/order-confirmation" element={<OrderConfirmation />} />
                <Route path="/promotion-checkout/:promotionId" element={
                  <RestaurantProtectedRoute>
                    <PromotionCheckout />
                  </RestaurantProtectedRoute>
                } />
                
                {/* Routes de login */}
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/business/login" element={<RestaurantLogin />} />
                <Route path="/restaurant-login" element={<Navigate to="/business/login" replace />} />
            
                {/* Routes ADMIN */}
                <Route path="/admin" element={
                  <PermissionProtectedRoute permission="view_dashboard">
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/users" element={
                  <PermissionProtectedRoute permission="view_users">
                    <AdminLayout>
                      <AdminUsers />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/restaurants" element={
                  <PermissionProtectedRoute permission="view_restaurants">
                    <AdminLayout>
                      <AdminRestaurants />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />

                <Route path="/admin/grocery-shops" element={
                  <PermissionProtectedRoute permission="view_restaurants">
                    <AdminLayout>
                      <AdminGroceryShops />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />

                <Route path="/admin/reference-data" element={
                  <PermissionProtectedRoute permission="view_restaurants">
                    <AdminLayout>
                      <AdminReferenceData />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/dishes" element={
                  <PermissionProtectedRoute permission="view_dishes">
                    <AdminLayout>
                      <AdminDishes />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/orders" element={
                  <PermissionProtectedRoute permission="view_orders">
                    <AdminLayout>
                      <AdminOrders />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/complaints" element={
                  <PermissionProtectedRoute permission="view_complaints">
                    <AdminLayout>
                      <AdminComplaints />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/payments" element={
                  <PermissionProtectedRoute permission="view_payments">
                    <AdminLayout>
                      <AdminPayments />
                    </AdminLayout>
                  </PermissionProtectedRoute>
                } />
                
                <Route path="/admin/admins" element={
                  <SuperAdminProtectedRoute>
                    <AdminLayout>
                      <AdminAdmins />
                    </AdminLayout>
                  </SuperAdminProtectedRoute>
                } />
                
                <Route path="/admin/settings" element={
                  <SuperAdminProtectedRoute>
                    <AdminLayout>
                      <AdminSettings />
                    </AdminLayout>
                  </SuperAdminProtectedRoute>
                } />
                
                <Route path="/admin/promotion-insights" element={
                  <SuperAdminProtectedRoute>
                    <AdminLayout>
                      <AdminPromotionInsights />
                    </AdminLayout>
                  </SuperAdminProtectedRoute>
                } />
                
                <Route path="/admin/promotion-pricing" element={
                  <SuperAdminProtectedRoute>
                    <AdminLayout>
                      <AdminPromotionPricing />
                    </AdminLayout>
                  </SuperAdminProtectedRoute>
                } />
                <Route path="/admin/announcements" element={
                  <SuperAdminProtectedRoute>
                    <AdminLayout>
                      <AdminAnnouncements />
                    </AdminLayout>
                  </SuperAdminProtectedRoute>
                } />
                
                {/* Routes MANAGEMENT (restaurateurs) - Version simplifiée */}
                <Route path="/management" element={
                  <RestaurantProtectedRoute>
                    <RestaurantLayout />
                  </RestaurantProtectedRoute>
                }>
                  <Route index element={<NewRestaurantDashboard />} />
                  <Route path="orders" element={<NewRestaurantOrders />} />
                  <Route path="menu" element={<RestaurantMenu />} />
                  <Route path="grocery" element={<RestaurantGrocery />} />
                  <Route path="catering" element={<NewRestaurantCatering />} />
                  <Route path="catering/offers/:serviceId" element={<CateringServiceOfferDetail />} />
                  <Route path="promotions" element={<NewRestaurantPromotions />} />
                  <Route path="analytics" element={<NewRestaurantAnalytics />} />
                  <Route path="wallet" element={<NewRestaurantWallet />} />
                  <Route path="employees" element={<NewRestaurantEmployees />} />
                  <Route path="reviews" element={<NewRestaurantReviews />} />
                  <Route path="announcements" element={<RestaurantAnnouncements />} />
                  <Route path="settings" element={<NewRestaurantSettings />} />
                </Route>
                
                {/* Page 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </ErrorBoundary>
            </div>
          </RestaurantAuthProvider>
        </AdminAuthProvider>
      </CartProvider>
    </Router>
  );
}

export default App;












